import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Sparkles, MapPin, Clock, Car, Gift, Calendar } from 'lucide-react';

const EventDetails = () => {
  const { t, isBangla, hasSelectedLanguage } = useLanguage();

  if (!hasSelectedLanguage) return null;

  return (
    <section 
      id="event-details" 
      className="py-16 sm:py-20 md:py-28 px-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #FFF8F0 0%, #F5E6D3 100%)'
      }}
      data-testid="event-details-section"
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -left-20 w-40 h-40 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-60 h-60 bg-[#800020]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Chapter Indicator */}
        <motion.div 
          className="text-center mb-6 flex items-center justify-center gap-3"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          <span className={`text-[#D4AF37] text-sm tracking-[0.2em] sm:tracking-[0.3em] uppercase font-light ${isBangla ? 'font-bangla' : ''}`}>
            {t('chapterOne')}
          </span>
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
        </motion.div>

        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14">
          <motion.div 
            className="w-16 sm:w-20 mx-auto h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mb-6"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8 }}
          />
          <motion.h2 
            className={`font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-[#800020] mb-3 px-4 ${isBangla ? 'font-bangla leading-snug' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="event-title"
          >
            {t('eventTitle')}
          </motion.h2>
          <motion.div 
            className="w-16 sm:w-20 mx-auto h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mt-6"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
        </div>

        {/* Event Cards */}
        <div className="grid gap-4 sm:gap-6 md:grid-cols-3">
          {/* Date & Time */}
          <motion.div
            className="bg-white/80 backdrop-blur-sm border border-[#E6C288] rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-shadow text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="w-12 sm:w-14 h-12 sm:h-14 mx-auto mb-4 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-full flex items-center justify-center">
              <Clock className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
            </div>
            <h3 className={`font-heading text-lg sm:text-xl text-[#800020] font-semibold mb-2 ${isBangla ? 'font-bangla' : ''}`}>
              {t('dateTimeLabel')}
            </h3>
            <p className={`text-[#2D2424]/70 text-sm sm:text-base ${isBangla ? 'font-bangla leading-relaxed' : ''}`}>
              {t('dateTime')}
            </p>
          </motion.div>

          {/* Location */}
          <motion.div
            className="bg-white/80 backdrop-blur-sm border border-[#E6C288] rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-shadow text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="w-12 sm:w-14 h-12 sm:h-14 mx-auto mb-4 bg-gradient-to-br from-[#800020] to-[#600018] rounded-full flex items-center justify-center">
              <MapPin className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
            </div>
            <h3 className={`font-heading text-lg sm:text-xl text-[#800020] font-semibold mb-2 ${isBangla ? 'font-bangla' : ''}`}>
              {t('locationLabel')}
            </h3>
            <p className={`text-[#2D2424]/70 text-sm sm:text-base ${isBangla ? 'font-bangla leading-relaxed' : ''}`}>
              {t('location')}
            </p>
          </motion.div>

          {/* Parking */}
          <motion.div
            className="bg-white/80 backdrop-blur-sm border border-[#E6C288] rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-shadow text-center md:col-span-1"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="w-12 sm:w-14 h-12 sm:h-14 mx-auto mb-4 bg-gradient-to-br from-[#2F4F4F] to-[#1a2f2f] rounded-full flex items-center justify-center">
              <Car className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
            </div>
            <h3 className={`font-heading text-lg sm:text-xl text-[#800020] font-semibold mb-2 ${isBangla ? 'font-bangla' : ''}`}>
              {t('parkingLabel')}
            </h3>
            <p className={`text-[#2D2424]/70 text-sm sm:text-base ${isBangla ? 'font-bangla leading-relaxed' : ''}`}>
              {t('parkingInfo')}
            </p>
          </motion.div>
        </div>

        {/* RSVP By Date - Full Width */}
        <div className="mt-4 sm:mt-6">
          <motion.div
            className="bg-white/80 backdrop-blur-sm border border-[#E6C288] rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-shadow text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="w-12 sm:w-14 h-12 sm:h-14 mx-auto mb-4 bg-gradient-to-br from-[#800020] to-[#600018] rounded-full flex items-center justify-center">
              <Calendar className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
            </div>
            <h3 className={`font-heading text-lg sm:text-xl text-[#800020] font-semibold mb-2 ${isBangla ? 'font-bangla' : ''}`}>
              {t('rsvpByLabel')}
            </h3>
            <p className={`text-[#2D2424]/70 text-sm sm:text-base ${isBangla ? 'font-bangla leading-relaxed' : ''}`}>
              {t('rsvpByDate')}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default EventDetails;
